import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownload from "@mui/icons-material/FileDownload";
import {
  useDeleteUsersByIdMutation,
  useLazyExportUsersQuery,
  useLazyGetUserListQuery,
  useUpdateUsersByIdMutation,
} from "../../services/user";
import { showError } from "../../constants/toasts";
import dayjs from "dayjs";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../components/DeleteModal";
import { showToast } from "../../helpers";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import { USER_TYPE } from "../../constants/enums";
import SwitchToggle from "../../components/SwitchToggle";

export interface UserListResponse {
  _id: string;
  fullName: string;
  email: string;
  phoneNo: string;
  dialCode: string;
  memberAccount: string;
  dob: string;
}

const UserList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [status, setStatus] = useState<string>("0");
  const [userType, setUserType] = useState<string>("0");
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [UsersData, setUsersData] = useState<any[]>([]);

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };
  const handleUserTypeChange = (event: SelectChangeEvent) => {
    setUserType(event.target.value as string);
  };

  const [getUserList] = useLazyGetUserListQuery();
  const [exportUsers] = useLazyExportUsersQuery();
  const [deleteUser] = useDeleteUsersByIdMutation();
  const [updateUser] = useUpdateUsersByIdMutation();

  const toggleDeleteModal = () => {
    setOpenDelete(!openDelete);
  };

  const handleDelete = async () => {
    try {
      await deleteUser({ id: activeId }).unwrap();
      toggleDeleteModal();
      fetchList();
      showToast("User deleted successfuly");
    } catch (e) {
      console.log(e);
    }
  };

  const handleToggle = async (user: any, nextChecked: boolean) => {
    const id = user?._id || user?.id;
    if (!id) return;
    const nextIsDeleted = nextChecked;
    try {
      await updateUser({
        body: { isDeleted: nextIsDeleted } as any,
        id,
      }).unwrap();
      showToast(`User ${nextIsDeleted ? "Deleted" : "Restored"}`);
      fetchList();
    } catch (e) {
      console.log(e);
    }
  };

  const fetchList = async () => {
    try {
      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
        isBlocked: status,
        isDeleted: status,
        userType: userType,
        role: "1",
      };
      const response = await getUserList(params).unwrap();
      if (response?.statusCode === 200) {
        setUsersData(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Users");
    }
  };

  const exportUsersCall = async () => {
    try {
      const response = await exportUsers({
        search: debouncedSearchTerm,
        isDeleted: status,
        userType: userType,
        role: "1",
      }).unwrap();
      if (response?.statusCode === 200) {
        if (response?.data) {
          window.open(response?.data, "_blank");
        }
        showToast("Users exported successfully");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to export Users");
    }
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || 0) / (pageSize || 10))
  );

  useEffect(() => {
    fetchList();
  }, [debouncedSearchTerm, page, pageSize, status, userType]);

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            placeholder="Search..."
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            value={searchTerm}
            onCross={() => setSearchTerm("")}
            onChange={(val: any) => {
              if (isValidInput(val.target.value)) {
                setSearchTerm(val.target.value);
              }
            }}
          />
          <div className=" form">
            <Select
              fullWidth
              value={status}
              displayEmpty
              onChange={handleStatusChange}
            >
              <MenuItem disabled value="">
                Select Status
              </MenuItem>
              <MenuItem value="0">All</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              {/* <MenuItem value="2">Suspended</MenuItem> */}
              <MenuItem value="3">Deleted</MenuItem>
            </Select>
          </div>
          <div className=" form">
            <Select
              fullWidth
              value={userType}
              onChange={handleUserTypeChange}
              displayEmpty
            >
              <MenuItem disabled value="">
                Select Source
              </MenuItem>
              <MenuItem value="0">All</MenuItem>
              <MenuItem value="1">APP</MenuItem>
              <MenuItem value="2">ERP</MenuItem>
            </Select>
          </div>
        </div>
        <div className="right_s">
          <Button
            onClick={() => navigate("/manage-users/add")}
            className="btn btn_primary"
          >
            <AddIcon /> Add User
          </Button>
          <Button onClick={exportUsersCall} className="btn btn_primary">
            <FileDownload /> Export User
          </Button>
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>DOB</TableCell>
              <TableCell>Member Account</TableCell>
              {/* <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell> */}
              <TableCell>Membership Card</TableCell>
              <TableCell>Remaining Redeemable Points</TableCell>
              <TableCell>Source (APP/ERP)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {UsersData.length ? (
              UsersData?.map((user) => (
                <TableRow hover key={user?._id}>
                  <TableCell>
                    <Box
                      component="a"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/manage-users/detail/${user?._id || ""}`, {
                          state: { user },
                        });
                      }}
                      className="user_block click_action"
                    >
                      <p>
                        <b>{user?.fullName || "-"}</b>
                        <span>
                          {user?.email || ""}{" "}
                          {user?.email ? (
                            <img
                              src="/static/images/tick_icon.svg"
                              alt="verified"
                            />
                          ) : (
                            "-"
                          )}
                        </span>
                        <span>{`${user?.dialCode} ${user?.phoneNo}`}</span>
                      </p>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user?.dateOfBirth
                      ? dayjs(user?.dateOfBirth).format("DD-MM-YYYY")
                      : "--"}
                  </TableCell>
                  <TableCell>{user?.memberAccount || "-"}</TableCell>
                  {/* <TableCell>{`${user?.dialCode || ""} ${
                    user?.phoneNo || ""
                  }`}</TableCell>
                  <TableCell>{user?.email || ""}</TableCell> */}
                  <TableCell>{user?.membershipCard || "-"}</TableCell>
                  <TableCell>
                    {user?.transactionDetails?.redeemablePoints || "-"}
                  </TableCell>
                  <TableCell>
                    {user?.userType === USER_TYPE.APP
                      ? "APP"
                      : user?.userType === USER_TYPE.ERP
                      ? "ERP"
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {user?.isDeleted ? "Deleted" : "Active"}
                  </TableCell>
                  <TableCell>
                    <div className="table_action_wrapper">
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() =>
                            navigate(
                              `/manage-users/detail/${user?._id || ""}`,
                              { state: { user } }
                            )
                          }
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        {/* {user?.isDeleted && ( */}
                        {/* <SwitchToggle
                          checked={!user?.isDeleted}
                          onChange={(checked) => handleToggle(user, !checked)}
                        /> */}
                        {/* )} */}
                        {/* <IconButton
                            onClick={() =>
                              navigate(`/manage-users/edit/${user?._id || ''}`, { state: { user } })
                            }
                          >
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton> */}
                        {/* <IconButton onClick={() => {
                            setActiveId(user?._id || '');
                            toggleDeleteModal();
                          }}>
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton> */}
                      </div>
                      {/* <Button
                          className="btn btn_primary sm"
                          onClick={() => setOpenModal(true)}
                        >
                          Verification
                        </Button> */}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>No Data Found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        module={UsersData || []}
        page={page}
        setPage={setPage}
        limit={pageSize}
        setLimit={setPageSize}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />

      {/* <VerificationModal
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
      /> */}
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this user?"
      />
    </>
  );
};

export default UserList;
